import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { OLButton } from "OLButton";
import { Tooltip } from "components/Tooltips/Tooltip";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useContextMenu } from "hooks/contextMenu";
import { useDebounce } from "hooks/useDebounce";
import { useIsOverflown } from "hooks/useIsOverflown";
import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { Search } from "./Select/Search";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    list: {
      paddingTop: 0,
    },
    itemsWrapper: ({ maxMenuHeight }: { maxMenuHeight: number }) => ({
      maxHeight: `${maxMenuHeight}px`,
      overflowY: "auto",
      padding: 0,
      margin: 0,
    }),
    noOption: {
      color: theme.gray400,
    },
    item: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(6),
      borderRadius: "8px",
      margin: theme.spacing(0, 4),
      padding: theme.spacing(4),
    },
    menu: {
      boxShadow:
        "0px 1px 18px 0px rgba(0, 0, 0, 0.12), 0px 6px 11px 0px rgba(0, 0, 0, 0.14), 0px 3px 7px -1px rgba(0, 0, 0, 0.20)",
    },
    paper: {
      overflowY: "hidden",
    },
    ellipsisOverflow: {
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    link: {
      textDecoration: "none",
      color: "inherit",
      width: "100%",
      height: "100%",
    },
  }),
);

interface Item {
  label: string;
  onClick?: () => void;
  icon?: React.ReactNode;
  customClass?: string;
  itemDataTestId?: string;
  isDisabled?: boolean;
  link?: {
    pathname: string;
    hash?: string;
    replace?: boolean;
    isExternal?: boolean;
  };
}

type Props = {
  buttonColor?:
    | "inherit"
    | "error"
    | "primary"
    | "secondary"
    | "info"
    | "success"
    | "warning"
    | undefined;
  buttonVariant?: "text" | "outlined" | "contained" | undefined;
  className?: string;
  "data-test-id"?: string;
  disabled?: boolean;
  hideSearch?: boolean;
  items?: Array<Item>;
  placeholder?: string;
  startIcon?: React.ReactElement;
  endIcon?: React.ReactElement;
  submit: string | React.ReactElement;
  maxMenuHeight?: number;
  horizontalPosition?: "left" | "right";
  button?: React.ComponentType;
  render?: (
    val: (event: React.MouseEvent<Element, MouseEvent>) => void,
  ) => React.ReactElement;
  size?: "small" | "medium" | "large";
  "data-sentry-unmask"?: boolean;
};

export function OLMenuButton({
  buttonColor = "primary",
  buttonVariant,
  className = "",
  "data-test-id": dataTestId,
  disabled = false,
  hideSearch = false,
  items = [],
  placeholder,
  startIcon,
  endIcon,
  submit,
  button: PropsButton,
  maxMenuHeight = 240,
  horizontalPosition = "left",
  render,
  size = "medium",
  ...props
}: Props) {
  const classes = useStyles({ maxMenuHeight });
  const contextMenu = useContextMenu();
  const [filter, setFilter] = useState<string>("");
  const { debouncedValue: searchQuery, isTyping } = useDebounce(filter);
  const { observeElement, isOverflown } = useIsOverflown();
  const CustomButton = PropsButton || OLButton;

  const filteredItems = items.filter((item) =>
    item.label?.toLowerCase().includes(searchQuery?.toLowerCase()),
  );

  const isAnyItem = filteredItems.length > 0;

  return (
    <>
      {!render ? (
        <CustomButton
          className={className}
          color={buttonColor}
          data-test-id={dataTestId}
          disabled={disabled}
          onClick={items.length ? contextMenu.handleMenuOpen : undefined}
          startIcon={startIcon}
          endIcon={endIcon}
          variant={buttonVariant}
          size={size}
        >
          <Tooltip
            text={submit}
            disableHoverListener={!isOverflown}
            placement="bottom-start"
          >
            <span className={classes.ellipsisOverflow} ref={observeElement}>
              {submit}
            </span>
          </Tooltip>
        </CustomButton>
      ) : (
        <>{render?.(contextMenu.handleMenuOpen)}</>
      )}

      <Menu
        anchorEl={contextMenu.anchor}
        anchorOrigin={{
          horizontal: horizontalPosition,
          vertical: "bottom",
        }}
        onClose={contextMenu.handleMenuClose}
        open={contextMenu.isMenuOpen}
        transformOrigin={{
          horizontal: horizontalPosition,
          vertical: "top",
        }}
        data-test-id={dataTestId}
        classes={{
          list: !hideSearch ? classes.list : "",
          paper: classes.paper,
        }}
        className={classes.menu}
      >
        {!hideSearch && (
          <Search
            placeholder={placeholder}
            searchedValue={filter}
            setSearchedValue={(val: string) => setFilter(val)}
            isLoading={isTyping}
            data-test-id={[dataTestId, "search-input"].join("-")}
          />
        )}

        <ul className={classes.itemsWrapper} role="listbox">
          {isAnyItem ? (
            filteredItems.map((item, index) => (
              <MenuItem
                className={`${classes.item} ${
                  index === 0 ? "MenuItemFirst" : ""
                } ${item.customClass || ""}`}
                data-test-id={
                  item.itemDataTestId ||
                  [dataTestId, "item", index + 1].join("-")
                }
                disabled={item.isDisabled}
                key={index}
                onClick={() => {
                  contextMenu.handleMenuClose();
                  item.onClick?.();
                }}
                data-sentry-unmask={props["data-sentry-unmask"]}
              >
                {item.link ? (
                  item.link.isExternal ? (
                    <a
                      href={item.link.pathname}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.link}
                    >
                      {item.icon ? item.icon : null}
                      {item.label}
                    </a>
                  ) : (
                    <NavLink
                      to={item.link}
                      replace={item.link.replace}
                      className={classes.link}
                    >
                      {item.icon ? item.icon : null}
                      {item.label}
                    </NavLink>
                  )
                ) : (
                  <>
                    {item.icon ? item.icon : null}
                    {item.label}
                  </>
                )}
              </MenuItem>
            ))
          ) : (
            <MenuItem className={classes.noOption}>No options</MenuItem>
          )}
        </ul>
      </Menu>
    </>
  );
}
